import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { meetingAPI, bodyAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Plus, Search, Calendar, Clock, MapPin, Video, 
  Filter, Loader2, ChevronRight, ChevronLeft, MoreVertical, Save
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameMonth, isToday, isSameDay } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const MeetingCalendar = ({ currentMonth, meetings, recurringDays, customSlots, onPrevMonth, onNextMonth, onDateClick, selectedMeetingId }) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPadding = getDay(monthStart);

  const STATUS_DOT_COLORS = {
    pending: 'bg-amber-400',
    scheduled: 'bg-emerald-500',
    completed: 'bg-blue-500',
    cancelled: 'bg-red-500',
    rescheduled: 'bg-purple-500',
  };

  const getMeetingsForDate = (date) => {
    const result = [];
    meetings.forEach(m => {
      try {
        const mDate = parseISO(m.date_time);
        if (isSameDay(mDate, date)) {
          result.push(m);
        }
        // For rescheduled meetings, also show on the new date
        if (m.status === 'rescheduled' && m.rescheduled_to) {
          const newDate = parseISO(m.rescheduled_to);
          if (isSameDay(newDate, date)) {
            result.push({ ...m, _isRescheduledNew: true });
          }
        }
      } catch (_) {}
    });
    return result;
  };

  const isRecurringDay = (date) => {
    return recurringDays.includes(getDay(date));
  };

  const isCustomSlot = (date) => {
    return customSlots.some(s => {
      try {
        return isSameDay(parseISO(s.date), date);
      } catch (_) { return false; }
    });
  };

  const recurringDayLabels = recurringDays
    .sort((a, b) => a - b)
    .map(d => DAY_NAMES[d])
    .join('/');

  return (
    <div data-testid="meeting-calendar">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#1A1D21]">Calendar</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onPrevMonth}
            className="p-1.5 rounded-lg hover:bg-[#F0F2F4] text-[#5A6570] transition-colors"
            data-testid="calendar-prev-month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-[#1A1D21] min-w-[120px] text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button
            onClick={onNextMonth}
            className="p-1.5 rounded-lg hover:bg-[#F0F2F4] text-[#5A6570] transition-colors"
            data-testid="calendar-next-month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_NAMES.map((d, i) => (
          <div
            key={d}
            className={`text-center text-xs font-medium py-1.5 ${
              recurringDays.includes(i) ? 'text-[#00A3A3] font-bold' : 'text-[#5A6570]'
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {/* Padding for first week */}
        {Array.from({ length: startPadding }).map((_, i) => (
          <div key={`pad-${i}`} className="h-10" />
        ))}

        {days.map(day => {
          const dayMeetings = getMeetingsForDate(day);
          const isRecurring = isRecurringDay(day);
          const isCustom = isCustomSlot(day);
          const today = isToday(day);
          const hasSelected = dayMeetings.some(m => m.id === selectedMeetingId);

          return (
            <div
              key={day.toISOString()}
              className={`relative h-10 flex flex-col items-center justify-center rounded-lg text-sm transition-colors cursor-pointer
                ${isRecurring ? 'bg-[#00A3A3]/10' : ''}
                ${isCustom && !isRecurring ? 'bg-[#F58A26]/10' : ''}
                ${today ? 'font-bold' : ''}
                ${hasSelected ? 'ring-2 ring-[#00A3A3]' : ''}
                ${dayMeetings.length > 0 ? 'hover:bg-[#F0F2F4]' : ''}
              `}
              onClick={() => dayMeetings.length > 0 && onDateClick && onDateClick(dayMeetings[0])}
              data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
            >
              <span className={`${today ? 'text-[#00A3A3]' : 'text-[#1A1D21]'}`}>
                {format(day, 'd')}
              </span>
              {dayMeetings.length > 0 && (
                <div className="flex gap-0.5 absolute bottom-0.5">
                  {dayMeetings.map((m, i) => (
                    <span
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT_COLORS[m.status] || 'bg-gray-400'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-[#E2E6EA]">
        <p className="text-[10px] font-semibold text-[#5A6570] mb-1.5">Legend</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {recurringDays.length > 0 && (
            <span className="flex items-center gap-1.5 text-[10px] text-[#5A6570]">
              <span className="w-3 h-3 rounded bg-[#00A3A3]/20 border border-[#00A3A3]/30" />
              {recurringDayLabels} (Blocked)
            </span>
          )}
          <span className="flex items-center gap-1.5 text-[10px] text-[#5A6570]">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            Pending — Awaiting confirmation
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-[#5A6570]">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Scheduled — Quorum obtained, will proceed
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-[#5A6570]">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            Completed — Meeting conducted
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-[#5A6570]">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            Cancelled — No rescheduling
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-[#5A6570]">
            <span className="w-2 h-2 rounded-full bg-purple-500" />
            Rescheduled — Moved to new date
          </span>
        </div>
      </div>
    </div>
  );
};


const Meetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const navigate = useNavigate();

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Schedule state
  const [schedule, setSchedule] = useState({
    recurring_days: [],
    default_time: '10:00',
    default_time_end: '12:00',
    custom_slots: [],
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [customSlotDialog, setCustomSlotDialog] = useState(false);
  const [newSlot, setNewSlot] = useState({ date: '', time: '10:00', label: '' });

  useEffect(() => {
    fetchData();
  }, [statusFilter, typeFilter]);

  const fetchData = async () => {
    try {
      const [meetingsRes, bodiesRes] = await Promise.all([
        meetingAPI.getAll(
          Object.fromEntries(
            Object.entries({ status: statusFilter, meeting_type: typeFilter })
              .filter(([_, v]) => v !== 'all')
          )
        ),
        bodyAPI.getAll(),
      ]);

      setMeetings(meetingsRes.data);
      setBodies(bodiesRes.data);

      // Fetch schedule
      try {
        const schedRes = await meetingAPI.getSchedule();
        if (schedRes.data) {
          setSchedule({
            recurring_days: schedRes.data.recurring_days || [],
            default_time: schedRes.data.default_time || '10:00',
            default_time_end: schedRes.data.default_time_end || '12:00',
            custom_slots: schedRes.data.custom_slots || [],
          });
        }
      } catch (_) {}
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    } finally {
      setLoading(false);
    }
  };

  const getBodyName = (bodyId) => {
    const body = bodies.find(b => b.id === bodyId);
    return body?.name || 'Unknown';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      scheduled: 'bg-green-100 text-green-800 border-green-300',
      completed: 'bg-blue-100 text-blue-800 border-blue-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
      rescheduled: 'bg-purple-100 text-purple-800 border-purple-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getTypeLabel = (type) => {
    const labels = { board: 'Board', committee: 'Committee', asm: 'ASM', ssm: 'SSM' };
    return labels[type] || type;
  };

  const filteredMeetings = meetings.filter(meeting =>
    meeting.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCalendarDateClick = (meeting) => {
    setSelectedMeetingId(meeting.id);
    const el = document.querySelector(`[data-testid="meeting-card-${meeting.id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleDelete = async (meetingId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this meeting?')) {
      try {
        await meetingAPI.delete(meetingId);
        setMeetings(meetings.filter(m => m.id !== meetingId));
      } catch (err) {
        console.error('Failed to delete meeting:', err);
      }
    }
  };

  const toggleRecurringDay = (dayNum) => {
    setSchedule(prev => ({
      ...prev,
      recurring_days: prev.recurring_days.includes(dayNum)
        ? prev.recurring_days.filter(d => d !== dayNum)
        : [...prev.recurring_days, dayNum],
    }));
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await meetingAPI.updateSchedule(schedule);
      setSchedule({
        recurring_days: res.data.recurring_days || [],
        default_time: res.data.default_time || '10:00',
        default_time_end: res.data.default_time_end || '12:00',
        custom_slots: res.data.custom_slots || [],
      });
      toast.success('Meeting schedule saved');
    } catch (err) {
      toast.error('Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleAddCustomSlot = () => {
    if (!newSlot.date) {
      toast.error('Please select a date');
      return;
    }
    const updated = {
      ...schedule,
      custom_slots: [...schedule.custom_slots, { ...newSlot }],
    };
    setSchedule(updated);
    setNewSlot({ date: '', time: '10:00', label: '' });
    setCustomSlotDialog(false);
    // Auto-save
    meetingAPI.updateSchedule(updated).then(() => {
      toast.success('Custom time slot added');
    }).catch(() => toast.error('Failed to save'));
  };

  const removeCustomSlot = (index) => {
    const updated = {
      ...schedule,
      custom_slots: schedule.custom_slots.filter((_, i) => i !== index),
    };
    setSchedule(updated);
    meetingAPI.updateSchedule(updated).catch(() => toast.error('Failed to save'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meetings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Meetings</h1>
          <p className="text-[#5A6570] mt-1">Manage your board, committee, and stockholders meetings</p>
        </div>
        <Link to="/meetings/new">
          <Button className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="create-meeting-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Meeting
          </Button>
        </Link>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Meetings List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
              <Input
                placeholder="Search meetings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#E2E6EA]"
                data-testid="search-meetings-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[160px] border-[#E2E6EA]" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2 text-[#5A6570]" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="rescheduled">Rescheduled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-[160px] border-[#E2E6EA]" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="board">Board</SelectItem>
                <SelectItem value="committee">Committee</SelectItem>
                <SelectItem value="asm">ASM</SelectItem>
                <SelectItem value="ssm">SSM</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Meetings List */}
          {filteredMeetings.length === 0 ? (
            <Card className="border-[#E2E6EA]">
              <CardContent className="py-16 text-center">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
                <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No meetings found</h3>
                <p className="text-[#5A6570] mb-4">
                  {searchTerm ? 'Try adjusting your search or filters' : 'Get started by scheduling your first meeting'}
                </p>
                <Link to="/meetings/new">
                  <Button className="bg-[#00A3A3] hover:bg-[#008585]">
                    <Plus className="h-4 w-4 mr-2" />
                    Schedule Meeting
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredMeetings.map((meeting) => (
                <Card 
                  key={meeting.id} 
                  className={`border-[#E2E6EA] hover:border-[#00A3A3] transition-colors cursor-pointer ${selectedMeetingId === meeting.id ? 'ring-2 ring-[#00A3A3] border-[#00A3A3]' : ''}`}
                  onClick={() => navigate(`/meetings/${meeting.id}`)}
                  data-testid={`meeting-card-${meeting.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <h3 className="font-semibold text-[#1A1D21] truncate text-sm">{meeting.title}</h3>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap ${getStatusColor(meeting.status)}`}>
                            {meeting.status.replace('_', ' ')}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[#5A6570]">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {format(parseISO(meeting.date_time), 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {format(parseISO(meeting.date_time), 'h:mm a')}
                          </span>
                          {meeting.venue && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {meeting.venue}
                            </span>
                          )}
                          {meeting.virtual_link && (
                            <span className="flex items-center gap-1">
                              <Video className="h-3.5 w-3.5" />
                              Virtual
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F0F2F4] text-[#5A6570]">
                            {getTypeLabel(meeting.meeting_type)}
                          </span>
                          <span className="text-[10px] text-[#5A6570]">
                            {getBodyName(meeting.body_id)}
                          </span>
                        </div>

                        {meeting.status === 'rescheduled' && meeting.rescheduled_to && (
                          <div className="mt-1.5 text-[10px] text-purple-700 bg-purple-50 px-2 py-1 rounded">
                            Rescheduled to {format(parseISO(meeting.rescheduled_to), 'MMM d, yyyy h:mm a')}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/meetings/${meeting.id}`)}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/meetings/${meeting.id}/agenda`)}>
                              Edit Agenda
                            </DropdownMenuItem>
                            {meeting.status === 'scheduled' && (
                              <DropdownMenuItem onClick={() => navigate(`/meetings/${meeting.id}/console`)}>
                                Start Meeting
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={(e) => handleDelete(meeting.id, e)}
                              className="text-red-600"
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <ChevronRight className="h-4 w-4 text-[#A8B1B9]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Right: Calendar + Schedule */}
        <div className="space-y-4">
          {/* Calendar Card */}
          <Card className="border-[#E2E6EA]">
            <CardContent className="p-4">
              <MeetingCalendar
                currentMonth={currentMonth}
                meetings={meetings}
                recurringDays={schedule.recurring_days}
                customSlots={schedule.custom_slots}
                onPrevMonth={() => setCurrentMonth(prev => subMonths(prev, 1))}
                onNextMonth={() => setCurrentMonth(prev => addMonths(prev, 1))}
                onDateClick={handleCalendarDateClick}
                selectedMeetingId={selectedMeetingId}
              />
            </CardContent>
          </Card>

          {/* Regular Schedule Config */}
          <Card className="border-[#E2E6EA]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#00A3A3]" />
                Block Regular Meeting Days
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Day Toggles */}
              <div className="grid grid-cols-4 gap-1.5">
                {DAY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => toggleRecurringDay(opt.value)}
                    className={`text-xs py-1.5 px-1 rounded-lg font-medium transition-colors ${
                      schedule.recurring_days.includes(opt.value)
                        ? 'bg-[#00A3A3] text-white'
                        : 'bg-[#F0F2F4] text-[#5A6570] hover:bg-[#E2E6EA]'
                    }`}
                    data-testid={`toggle-day-${opt.value}`}
                  >
                    {opt.label.slice(0, 3)}
                  </button>
                ))}
              </div>

              {/* Default Time Range */}
              <div className="space-y-1.5">
                <Label className="text-xs text-[#5A6570]">Default Time</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={schedule.default_time}
                    onChange={(e) => setSchedule(prev => ({ ...prev, default_time: e.target.value }))}
                    className="border-[#E2E6EA] h-9 text-sm flex-1"
                    data-testid="default-time-start"
                  />
                  <span className="text-xs text-[#5A6570]">to</span>
                  <Input
                    type="time"
                    value={schedule.default_time_end || ''}
                    onChange={(e) => setSchedule(prev => ({ ...prev, default_time_end: e.target.value }))}
                    className="border-[#E2E6EA] h-9 text-sm flex-1"
                    data-testid="default-time-end"
                  />
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveSchedule}
                disabled={savingSchedule}
                className="w-full bg-[#00A3A3] hover:bg-[#008585] h-9 text-sm"
                data-testid="save-schedule-btn"
              >
                {savingSchedule ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                Save Schedule
              </Button>
            </CardContent>
          </Card>

          {/* Custom Slots List */}
          {schedule.custom_slots.length > 0 && (
            <Card className="border-[#E2E6EA]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Custom Time Slots</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {schedule.custom_slots.map((slot, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-[#F8F9FA] rounded-lg">
                    <div>
                      <p className="text-xs font-medium text-[#1A1D21]">
                        {slot.date} at {slot.time}
                      </p>
                      {slot.label && (
                        <p className="text-[10px] text-[#5A6570]">{slot.label}</p>
                      )}
                    </div>
                    <button
                      onClick={() => removeCustomSlot(i)}
                      className="text-red-400 hover:text-red-600 text-xs"
                      data-testid={`remove-slot-${i}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Custom Slot Dialog */}
      <Dialog open={customSlotDialog} onOpenChange={setCustomSlotDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Custom Time Slot</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input
                type="date"
                value={newSlot.date}
                onChange={(e) => setNewSlot(prev => ({ ...prev, date: e.target.value }))}
                className="border-[#E2E6EA]"
                data-testid="custom-slot-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Time</Label>
              <Input
                type="time"
                value={newSlot.time}
                onChange={(e) => setNewSlot(prev => ({ ...prev, time: e.target.value }))}
                className="border-[#E2E6EA]"
                data-testid="custom-slot-time"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Label (Optional)</Label>
              <Input
                value={newSlot.label}
                onChange={(e) => setNewSlot(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., Special Board Meeting"
                className="border-[#E2E6EA]"
                data-testid="custom-slot-label"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomSlotDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddCustomSlot}
              className="bg-[#00A3A3] hover:bg-[#008585]"
              data-testid="confirm-add-slot-btn"
            >
              Add Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Meetings;
